import React, { FC, memo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getWarehouseInfo, getBoxInfo } from 'pages/BookingPage';
import { getContractOptions } from 'entities/Contract';
import { getLoggedUserData, useAuthorizedState, useCheckUserDataComplete } from 'entities/User';
import { ModernSummaryList } from 'widgets/ModernContractInformationForm';
import { ModernUserDetailsForm } from './ModernUserDetailsForm';
import { ModernUserDetailsFormMobile } from './ModernUserDetailsFormMobile';
import { SuccessScreen } from 'shared/ui/SuccessScreen';
import { Button } from 'shared/ui/Button';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';

interface ModernUserDetailsStepProps {
  onCompleteStep: () => void;
  onRentOptionsChange: () => void;
}

export const ModernUserDetailsStep: FC<ModernUserDetailsStepProps> = memo((props) => {
  const { onCompleteStep, onRentOptionsChange } = props;

  const { t } = useAppTranslation(['booking']);
  const warehouseInfo = useAppSelector(getWarehouseInfo);
  const boxInfo = useAppSelector(getBoxInfo);
  const contractOptions = useAppSelector(getContractOptions);
  const isAuthorized = useAuthorizedState();
  const isUserDataCompleted = useCheckUserDataComplete();
  const loggedUser = useAppSelector(getLoggedUserData);
  const { isDesktop } = useScreenBreakpoints();

  if (isAuthorized && isUserDataCompleted) {
    const fullName = `${loggedUser?.firstName ?? ''} ${loggedUser?.lastName ?? ''}`.trim();
    const displayName = fullName || loggedUser?.email || '';

    if (isDesktop) {
      return (
        <div className="flex gap-6">
          <div className="flex-1">
            <SuccessScreen
              title={t('Congratulations!')}
              description={t('You are already logged in as {{name}}', { name: displayName })}
              actions={<Button onClick={onCompleteStep}>{t('Go to payment step')}</Button>}
            />
          </div>
        </div>
      );
    }

    return (
      <SuccessScreen
        title={t('Congratulations!')}
        description={t('You are already logged in as {{name}}', { name: displayName })}
        actions={<Button onClick={onCompleteStep}>{t('Go to payment step')}</Button>}
      />
    );
  }

  if (isDesktop) {
    return (
      <div className="flex gap-6">
        <div className="flex-1">
          <ModernUserDetailsForm
            warehouseInfo={warehouseInfo}
            boxInfo={boxInfo}
            contractInfo={contractOptions}
            onCompleteStep={onCompleteStep}
            onRentOptionsChange={onRentOptionsChange}
          />
        </div>

        <div className="w-[350px] flex-shrink-0">
          <ModernSummaryList warehouseInfo={warehouseInfo} boxInfo={boxInfo} contractOptions={contractOptions} />
        </div>
      </div>
    );
  }

  return (
    <ModernUserDetailsFormMobile
      warehouseInfo={warehouseInfo}
      boxInfo={boxInfo}
      contractInfo={contractOptions}
      onCompleteStep={onCompleteStep}
      onRentOptionsChange={onRentOptionsChange}
    />
  );
});
